import React, { useEffect, useMemo, useRef, useState } from "react";
import crest from "../images/castleknock_logo.png";

function HomeAwayIcon() {
  return (
    <svg viewBox="0 0 24 24" className="labelSvg" aria-hidden="true">
      <path d="M4 10.5 12 4l8 6.5V20a1.5 1.5 0 0 1-1.5 1.5H5.5A1.5 1.5 0 0 1 4 20v-9.5Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M9.5 21.5V14a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v7.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

const FIELDS = [
  { key: "Date", label: "Date" },
  { key: "Time", label: "Time" },
  { key: "Competition", label: "Competition" },
  { key: "Code", label: "Code" },
  { key: "Opponent", label: "Opponent" },
  { key: "Home/Away", label: "Home/Away" },
  { key: "Location", label: "Location" },
  { key: "Referee", label: "Referee" },
];

const EXPORT_FIELDS = [
  { key: "Date", label: "Date" },
  { key: "Day", label: "Day" },
  { key: "Time", label: "Time" },
  { key: "Competition", label: "Competition" },
  { key: "Code", label: "Code" },
  { key: "Opponent", label: "Opponent" },
  { key: "Home/Away", label: "Home/Away" },
  { key: "Location", label: "Location" },
  { key: "Referee", label: "Referee" },
];

function parseCsv(text) {
  const rows = [];
  let row = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cur);
      cur = "";
    } else if (ch === "\n") {
      row.push(cur);
      rows.push(row);
      row = [];
      cur = "";
    } else if (ch === "\r") {
      // ignore
    } else {
      cur += ch;
    }
  }

  if (cur.length > 0 || row.length > 0) {
    row.push(cur);
    rows.push(row);
  }

  const cleaned = rows.filter((r) => r.some((cell) => String(cell || "").trim() !== ""));
  if (cleaned.length === 0) return { headers: [], rows: [] };

  const headers = cleaned[0].map((h) => String(h || "").replace(/^\uFEFF/, "").trim());
  const data = cleaned.slice(1).map((cells) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = cells[idx] ?? "";
    });
    return obj;
  });

  return { headers, rows: data };
}

function normalizeReferee(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^tbc\b/i.test(text) && /pending/i.test(text)) return "TBC";
  return text;
}

function fieldClassName(key) {
  return `cell-${String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}`;
}

const INLINE_MOBILE_FIELDS = new Set(["Time", "Competition", "Opponent", "Home/Away"]);

function getUpcomingRange(mode) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);

  if (mode === "next2w") {
    const end = new Date(start);
    end.setDate(end.getDate() + 14);
    const monday = nextMonday(end);
    monday.setHours(23, 59, 59, 999);
    return { start, end: monday };
  }

  return { start, end: null };
}

function nextMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const offset = (8 - day) % 7;
  d.setDate(d.getDate() + offset);
  d.setHours(0, 0, 0, 0);
  return d;
}

function parseDateValue(text, fallbackYear) {
  const raw = String(text || "").trim();
  const numeric = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/.exec(raw);
  if (numeric) {
    const day = Number(numeric[1]);
    const month = Number(numeric[2]);
    let year = Number(numeric[3]);
    if (year < 100) year += 2000;
    const d = new Date(year, month - 1, day);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }

  const cleaned = raw
    .replace(/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+/i, "")
    .replace(/(\d+)(st|nd|rd|th)/gi, "$1")
    .trim();

  if (!cleaned) return null;

  const withYear = /\b\d{4}\b/.test(cleaned) ? cleaned : `${cleaned} ${fallbackYear}`;
  const value = Date.parse(withYear);
  return Number.isNaN(value) ? null : value;
}

function formatDateParts(text, fallbackYear) {
  const value = parseDateValue(text, fallbackYear);
  if (!value) return { date: text || "-", day: "" };
  const d = new Date(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const day = new Intl.DateTimeFormat("en-IE", { weekday: "long" }).format(d);
  return { date: `${dd}/${mm}/${yy}`, day };
}

function dayFromDate(text, fallbackYear) {
  const value = parseDateValue(text, fallbackYear);
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("en-IE", { weekday: "long" }).format(new Date(value));
  } catch (err) {
    return "";
  }
}

function parseTimeValue(text) {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(text || "").trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function parseDateTimeValue(row, fallbackYear) {
  const dateValue = parseDateValue(row?.Date, fallbackYear);
  if (!dateValue) return null;
  const timeMinutes = parseTimeValue(row?.Time);
  const base = new Date(dateValue);
  if (timeMinutes === null || Number.isNaN(timeMinutes)) return base.getTime();
  base.setHours(Math.floor(timeMinutes / 60), timeMinutes % 60, 0, 0);
  return base.getTime();
}

function sortRows(rows, sort, fallbackYear) {
  const { key, dir } = sort;
  const mult = dir === "asc" ? 1 : -1;

  const getValue = (row) => {
    const raw = row[key];

    if (key === "Date" || key === "Time") {
      const v = parseDateTimeValue(row, fallbackYear);
      return v ?? String(raw || "");
    }

    return String(raw || "");
  };

  return [...(rows || [])].sort((a, b) => {
    const av = getValue(a);
    const bv = getValue(b);

    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * mult;
    }

    return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * mult;
  });
}

function formatTimestamp(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("en-IE", { dateStyle: "medium", timeStyle: "short" }).format(value);
  } catch (err) {
    return value.toLocaleString();
  }
}

function formatCompetitionDisplay(value) {
  let text = String(value || "").trim();
  if (!text) return "";

  text = text.replace(/\bGo Ahead Ireland\b/gi, "");
  text = text.replace(/\bUnder\s*(\d+)/gi, "U$1");
  text = text.replace(/\bDivision\b/gi, "Div.");

  text = text.replace(/\bAdult Football League\b/gi, "AFL");
  text = text.replace(/\bAdult Hurling League\b/gi, "AHL");
  text = text.replace(/\bMinor Hurling League\b/gi, "MHL");
  text = text.replace(/\bMinor Football League\b/gi, "MFL");

  const hasLeagueOrAbbrev = /\bLeague\b/i.test(text) || /\b(AFL|AHL|MFL|MHL)\b/.test(text);
  if (hasLeagueOrAbbrev) {
    text = text.replace(/\s*Div(?:ision)?\.?\s*/gi, " ");
  }

  text = text.replace(/\s{2,}/g, " ").trim();
  return text;
}

export default function FixturesScraper() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastCount, setLastCount] = useState(null);
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState({ key: "Date", dir: "asc" });
  const [lastScraped, setLastScraped] = useState(null);
  const [homeAway, setHomeAway] = useState("All");
  const range = useMemo(() => getUpcomingRange("all"), []);
  const [competitionFilter, setCompetitionFilter] = useState("All");
  const [codeFilter, setCodeFilter] = useState("All");
  const [toast, setToast] = useState("");
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [isMobile, setIsMobile] = useState(false);
  const toastTimer = useRef(null);
  const skeletonRows = useMemo(() => Array.from({ length: 6 }), []);

  const fallbackYear = "2026";

  useEffect(() => {
    setExpandedRows(new Set());
  }, [rows]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const media = window.matchMedia("(max-width: 820px)");
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);

    if (media.addEventListener) {
      media.addEventListener("change", handleChange);
      return () => media.removeEventListener("change", handleChange);
    }

    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = (message) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setToast("");
    }, 2600);
  };

  const competitionOptions = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((row) => {
      const code = String(row.Code || "").trim();
      if (codeFilter !== "All" && code !== codeFilter) return;
      const comp = String(row.Competition || "").trim();
      if (!comp) return;
      if (!map.has(comp)) map.set(comp, comp);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [rows, codeFilter]);

  useEffect(() => {
    if (competitionFilter !== "All" && !competitionOptions.includes(competitionFilter)) {
      setCompetitionFilter("All");
    }
  }, [competitionOptions, competitionFilter]);

  const filteredRows = useMemo(() => {
    const base = homeAway === "All"
      ? rows
      : (rows || []).filter((r) => String(r["Home/Away"] || "").trim() === homeAway);

    const start = range.start.getTime();
    const end = range.end ? range.end.getTime() : null;
    const code = codeFilter;
    const competition = competitionFilter;

    return (base || []).filter((r) => {
      if (competition !== "All") {
        const rowCompetition = String(r.Competition || "").trim();
        if (rowCompetition !== competition) return false;
      }
      if (code !== "All") {
        const rowCode = String(r.Code || "").trim();
        if (rowCode !== code) return false;
      }
      const ts = parseDateTimeValue(r, fallbackYear);
      if (ts === null) return false;
      if (ts < start) return false;
      if (end !== null && ts > end) return false;
      return true;
    });
  }, [rows, homeAway, competitionFilter, codeFilter, fallbackYear, range]);

  const sortedRows = useMemo(
    () => sortRows(filteredRows, sort, fallbackYear),
    [filteredRows, sort, fallbackYear]
  );


  const pullFixtures = async () => {
    setLoading(true);
    setError("");
    setLastCount(null);

    try {
      const res = await fetch("/.netlify/functions/fixturesScraper");
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to fetch fixtures.");
      }

      const csv = await res.text();
      const parsed = parseCsv(csv);
      const cleanedRows = (parsed.rows || []).map((row) => ({
        ...row,
        Referee: normalizeReferee(row.Referee),
      }));
      setRows(cleanedRows);

      const count = Math.max(0, (cleanedRows || []).length);
      setLastCount(count);
      setLastScraped(new Date());

      let syncMessage = "";
      try {
        const syncRes = await fetch("/.netlify/functions/fixturesIngest");
        if (!syncRes.ok) {
          const msg = await syncRes.text();
          throw new Error(msg || "Failed to sync fixtures.");
        }
        const payload = await syncRes.json();
        syncMessage = ` Synced ${payload.total || 0} fixtures.`;
      } catch (syncErr) {
        setError(syncErr.message || "Failed to sync fixtures.");
      }

      showToast(`Loaded ${count} fixtures.` + syncMessage);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to fetch fixtures.");
    } finally {
      setLoading(false);
    }
  };

  const csvEscape = (value) => {
    const s = String(value ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const buildCsv = (dataRows, fields) => {
    const header = fields.map((f) => f.label).join(",");
    const lines = (dataRows || []).map((row) =>
      fields.map((f) => {
        if (f.key === "Day") {
          const value = row.Day || dayFromDate(row.Date, fallbackYear);
          return csvEscape(value ?? "");
        }
        return csvEscape(row[f.key] ?? "");
      }).join(",")
    );
    return [header, ...lines].join("\n");
  };

  const downloadCsv = (dataRows, filename) => {
    const csv = buildCsv(dataRows, EXPORT_FIELDS);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadAllFixtures = () => {
    if (!rows || rows.length === 0) return;
    const ordered = sortRows(rows, { key: "Date", dir: "asc" }, fallbackYear);
    const filename = "castleknock_fixtures_all.csv";
    downloadCsv(ordered, filename);
    showToast(`Downloaded ${ordered.length} rows to ${filename}.`);
  };

  const downloadFilteredFixtures = () => {
    if (!sortedRows || sortedRows.length === 0) return;
    const filename = "castleknock_fixtures_filtered.csv";
    downloadCsv(sortedRows, filename);
    showToast(`Downloaded ${sortedRows.length} rows to ${filename}.`);
  };

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const arrow = (key) => {
    if (sort.key !== key) return "-";
    return sort.dir === "asc" ? "^" : "v";
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  return (
    <section className="page">
      <div className="pageHeader">
        <h2 className="pageTitle">Fixtures Scraper</h2>
        <div className="pageDesc">
          Pulls Castleknock fixtures from Sportlomo and downloads a CSV.
        </div>
        <div className="pageActions">
          <button className="ghostBtn" onClick={pullFixtures} disabled={loading}>
            {loading ? "Loading fixtures..." : "Load fixtures"}
          </button>
          <button className="ghostBtn" onClick={downloadAllFixtures} disabled={loading || rows.length === 0}>
            Export Fixtures
          </button>
          <button className="ghostBtn" onClick={downloadFilteredFixtures} disabled={loading || sortedRows.length === 0}>
            Export Fixtures (Filtered)
          </button>
        </div>
      </div>

      {loading && (
        <div className="loadingPanel" role="status" aria-live="polite">
          <div className="loadingBadge">
            <div className="loaderRing" aria-hidden="true" />
            <div className="loaderCrest" aria-hidden="true">
              <img src={crest} alt="" />
            </div>
          </div>
          <div className="loadingText">
            <div className="loadingTitle">Castleknock GAA Live Scrape</div>
            <div className="loadingSub">Pulling fixtures from Sportlomo…</div>
          </div>
          <div className="loadingPulse" aria-hidden="true" />
        </div>
      )}

      <div className="controls" style={{ marginTop: 0 }}>
        <div className="field">
          <label className="label" htmlFor="codeFilter">
            Code
          </label>
          <select
            id="codeFilter"
            className="select"
            value={codeFilter}
            onChange={(e) => setCodeFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Mens Football">Mens Football</option>
            <option value="Boys Football">Boys Football</option>
            <option value="Girls Football">Girls Football</option>
            <option value="Girls Camogie">Girls Camogie</option>
            <option value="Ladies Football">Ladies Football</option>
            <option value="Ladies Camogie">Ladies Camogie</option>
            <option value="Mens Hurling">Mens Hurling</option>
            <option value="Boys Hurling">Boys Hurling</option>
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="competitionFilter">
            Competition
          </label>
          <select
            id="competitionFilter"
            className="select"
            value={competitionFilter}
            onChange={(e) => setCompetitionFilter(e.target.value)}
          >
            <option value="All">All competitions</option>
            {competitionOptions.map((comp) => (
              <option key={comp} value={comp}>{formatCompetitionDisplay(comp)}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="homeAway">
            <span className="labelIcon" aria-hidden="true"><HomeAwayIcon /></span>
            Home/Away
          </label>
          <select
            id="homeAway"
            className="select"
            value={homeAway}
            onChange={(e) => setHomeAway(e.target.value)}
          >
            <option value="Home">Home</option>
            <option value="Away">Away</option>
            <option value="All">All</option>
          </select>
        </div>
      </div>

      {lastScraped && (
        <div className="pill">Last scraped: {formatTimestamp(lastScraped)}</div>
      )}

      {error && (
        <div className="pill" style={{ borderColor: "rgba(255,80,80,.4)" }}>
          {error}
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="tableWrap scraperFixtures skeleton">
          <div className="tableScroll">
            <div className="tableHeader statsHeader">
              {FIELDS.map((field) => (
                <div key={field.key} className="thBtn thGhost">
                  <span>{field.label}</span>
                  <span className="thArrow">-</span>
                </div>
              ))}
            </div>
            <div className="tableBody">
              {skeletonRows.map((_, idx) => (
                <div className="tableRow" key={`sk-${idx}`}>
                  {FIELDS.map((field) => (
                    <div className={`cell skeletonCell ${fieldClassName(field.key)}`} key={field.key} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && filteredRows.length === 0 && lastCount !== null && (
        <div className="pill">No fixtures found.</div>
      )}

      {filteredRows.length > 0 && (
        <div className="tableWrap scraperFixtures">
          <div className="tableScroll">
            <div className="tableHeader statsHeader">
              {FIELDS.map((field) => (
                <button
                  key={field.key}
                  type="button"
                  className="thBtn"
                  onClick={() => toggleSort(field.key)}
                  aria-label={`Sort by ${field.label}`}
                  title={`Sort by ${field.label}`}
                >
                  <span>{field.label}</span>
                  <span className={`thArrow ${sort.key === field.key ? "on" : ""}`}>{arrow(field.key)}</span>
                </button>
              ))}
            </div>

            <div className="tableBody">
              {sortedRows.map((row, idx) => {
                const rowId = `${row.Date || ""}-${row.Time || ""}-${row.Opponent || ""}-${idx}`;
                const isExpanded = expandedRows.has(rowId);
                return (
                  <div
                    className={`tableRow ${isExpanded ? "expanded" : ""}`}
                    key={rowId}
                  >
                  {FIELDS.map((field) => {
                    const raw = row[field.key] || "";
                    if (field.key === "Date") {
                      const parts = formatDateParts(raw, fallbackYear);
                      return (
                        <div
                          className={`cell dateCell ${fieldClassName(field.key)}`}
                          key={field.key}
                          data-label={field.label}
                        >
                          <div className="dateMain">{parts.date}</div>
                          <div className="dateSub">{parts.day}</div>
                        </div>
                      );
                    }
                    let value = raw || "-";
                    if (field.key === "Competition") {
                      value = formatCompetitionDisplay(value);
                    }
                    const isInlineMobile = isMobile && INLINE_MOBILE_FIELDS.has(field.key);
                    return (
                      <div
                        className={`cell ${fieldClassName(field.key)} ${isInlineMobile ? "inlineCell" : ""}`}
                        key={field.key}
                        title={value}
                        data-label={field.label}
                      >
                        <div className="cellStack">
                          {isInlineMobile ? (
                            <>
                              <span className="inlineLabel">{field.label}:</span>
                              <span className="inlineValue">{value}</span>
                            </>
                          ) : (
                            <span>{value}</span>
                          )}
                          {field.key === "Competition" && isMobile && (
                            <button
                              type="button"
                              className="rowToggle"
                              onClick={() => toggleRow(rowId)}
                            >
                              {isExpanded ? "Show less" : "Show more"}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
              })}
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </section>
  );
}
